// Figma design: https://www.figma.com/design/sseCNpJYq9VS8RO8p4u6HJ/Ticket-Purchase-Flow?node-id=17209-48797

import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { showToast } from '@tix-ticketing/ui-components';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { DiscountClubDto, OrderDiscountClubDto, OrderResponse } from 'api/generated/types.gen';
import { DiscountClubDialog } from 'components/common/dialogs/discount-club-dialog';
import { useAppState } from 'lib/app-state';
import { svgDataUri } from 'lib/helpers/discount-club-form';
import { FormattedPrice } from 'lib/helpers/formatted-price';
import { getDiscountClubLogo, getKnownDiscountClub, useDiscountClubs } from 'lib/hooks/use-discount-clubs';
interface ClubPanel {
  name: string;
  club?: DiscountClubDto;
  applied?: OrderDiscountClubDto;
}

/** The club's greyscale logo, or its name where no logo is known. */
export const DiscountClubMark = ({
  name,
  height = 3
}: {
  name?: string | null;
  /** In theme spacing units. */
  height?: number;
}) => {
  // Subscribes to the offered clubs so the logo shows once they load.
  const {
    eventData
  } = useAppState();
  useDiscountClubs(eventData.order);
  const logo = svgDataUri(getDiscountClubLogo(name)?.greyscale);
  if (!logo) {
    return <Typography variant="body2Medium" sx={{
      color: 'text.secondary'
    }}>
        {name}
      </Typography>;
  }
  return <Box component="img" src={logo} alt={name ?? ''} sx={theme => ({
    height: theme.spacing(height),
    display: 'block',
    // The greyscale logo is drawn in black, so it is inverted to stay
    // legible on a dark surface.
    ...theme.applyStyles('dark', {
      filter: 'invert(1)'
    })
  })} />;
};

/**
 * One panel per discount club under the order summary: the clubs the order is
 * offered and the ones it already has cards for, joined by name, since that is
 * all the order carries. Another card can be added to an applied club while
 * its form is known; the backend answers when no ticket is left for it.
 */
export const CartDiscountClubs = ({
  order
}: {
  order?: OrderResponse;
}) => {
  const intl = useIntl();
  const {
    clubs
  } = useDiscountClubs(order);
  // The club stays set after closing so the dialog can animate out.
  const [dialogClub, setDialogClub] = useState<DiscountClubDto | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const panels: ClubPanel[] = [];
  clubs.forEach(club => panels.push({
    name: club.name ?? '',
    club
  }));
  (order?.discountClubs ?? []).forEach(applied => {
    const panel = panels.find(p => p.name === applied.name);
    if (panel) panel.applied = applied;else panels.push({
      name: applied.name ?? '',
      club: getKnownDiscountClub(applied.name),
      applied
    });
  });
  if (!order || panels.length === 0) return null;
  return <>
      {panels.map(({
      name,
      club,
      applied
    }) => <Stack key={name} data-testid="cart-discount-club" spacing={2} sx={theme => ({
      backgroundColor: theme.palette.containerBackground.main,
      p: 3,
      borderRadius: `${theme.containerRadius.main}px`
    })}>
          <Stack direction="row" sx={{
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
            <DiscountClubMark name={name} />
            {club && <Button data-testid="cart-add-discount-club-card" variant="contained" color="monochrome" size="small" onClick={() => {
          setDialogClub(club);
          setDialogOpen(true);
        }}>
                <FormattedMessage id="cart.discountClub.addCard" defaultMessage={[{
            "type": 0,
            "value": "Add card"
          }]} />
              </Button>}
          </Stack>
          {!!applied?.cards?.length && <Stack direction="row" spacing={2} sx={{
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
              <Stack direction="row" useFlexGap spacing={1} sx={{
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
                <Typography variant="body2" sx={{
            color: 'text.secondary'
          }}>
                  <FormattedMessage id="cart.discountClub.card" defaultMessage={[{
              "type": 0,
              "value": "Card"
            }]} />
                </Typography>
                {applied.cards.map((card, index) => <Chip key={`${card.card}-${index}`} data-testid="cart-applied-discount-club-card" variant="outlined" label={card.card} sx={{
            borderColor: 'monochrome.outlineRestingBorder',
            color: 'monochrome.contrastText'
          }} />)}
              </Stack>
              {/* A club that gives its discount as a fee reports no saving. */}
              {!!applied.discount && <Typography data-testid="cart-discount-club-total" variant="body2" sx={{
          color: 'text.secondary',
          whiteSpace: 'nowrap'
        }}>
                  <FormattedPrice amount={-applied.discount} />
                </Typography>}
            </Stack>}
        </Stack>)}
      {dialogClub && <DiscountClubDialog open={dialogOpen} onClose={() => setDialogOpen(false)} club={dialogClub} order={order} onApplied={() => showToast({
      title: intl.formatMessage({
        id: "cart.discountClub.appliedTitle",
        defaultMessage: [{
          "type": 1,
          "value": "club"
        }, {
          "type": 0,
          "value": " discount applied"
        }]
      }, {
        club: dialogClub.name
      }),
      message: intl.formatMessage({
        id: "cart.discountClub.appliedMessage",
        defaultMessage: [{
          "type": 0,
          "value": "Your cart has been updated with the discount."
        }]
      }),
      severity: 'success'
    })} />}
    </>;
};